/**
 * Small shared utilities. Kept dependency-free and minimal for Palier 0.
 */

// --- Byte formatting -------------------------------------------------------

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'] as const;

export function formatBytes(bytes: number, decimals = 1): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), BYTE_UNITS.length - 1);
  const unit = BYTE_UNITS[exponent] ?? 'B';
  const value = bytes / 1024 ** exponent;
  return `${value.toFixed(decimals)} ${unit}`;
}

// --- Time ------------------------------------------------------------------

/** Calendar month of a date in UTC, formatted 'YYYY-MM' (usage-quota bucket). */
export function utcMonth(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

// --- Domain handling -------------------------------------------------------

const DOMAIN_REGEX = /^(?!-)(?:[a-z0-9-]{1,63}(?<!-)\.)+[a-z]{2,63}$/i;

/** Strip protocol, path, port and a leading `www.` to get a bare hostname. */
export function normalizeDomain(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/[/?#].*$/, '')
    .replace(/:\d+$/, '')
    .replace(/^www\./, '');
}

/** Validate a bare domain (after normalisation). */
export function validateDomain(input: string): boolean {
  const domain = normalizeDomain(input);
  if (domain.length === 0 || domain.length > 253) return false;
  return DOMAIN_REGEX.test(domain);
}

// --- Minimal user-agent parsing -------------------------------------------
// A deliberately tiny heuristic for Palier 0. The full parser (200+ bots,
// device models, etc.) is implemented in Palier 1.

export type DeviceType = 'desktop' | 'mobile' | 'tablet' | 'bot' | 'unknown';

export interface ParsedUserAgent {
  browser: string;
  os: string;
  deviceType: DeviceType;
}

export function parseUserAgent(userAgent: string): ParsedUserAgent {
  const ua = userAgent.toLowerCase();

  if (ua.length === 0) {
    return { browser: 'unknown', os: 'unknown', deviceType: 'unknown' };
  }

  const isBot = /bot|crawl|spider|slurp|curl|wget|python-requests|headless/.test(ua);
  const isTablet = /ipad|tablet/.test(ua);
  const isMobile = !isTablet && /mobi|iphone|android.*mobile/.test(ua);

  const deviceType: DeviceType = isBot
    ? 'bot'
    : isTablet
      ? 'tablet'
      : isMobile
        ? 'mobile'
        : 'desktop';

  let browser = 'unknown';
  if (ua.includes('edg/')) browser = 'Edge';
  else if (ua.includes('chrome/') && !ua.includes('chromium')) browser = 'Chrome';
  else if (ua.includes('firefox/')) browser = 'Firefox';
  else if (ua.includes('safari/') && !ua.includes('chrome/')) browser = 'Safari';

  let os = 'unknown';
  if (ua.includes('windows')) os = 'Windows';
  else if (ua.includes('mac os')) os = 'macOS';
  else if (ua.includes('android')) os = 'Android';
  else if (/iphone|ipad|ipod/.test(ua)) os = 'iOS';
  else if (ua.includes('linux')) os = 'Linux';

  return { browser, os, deviceType };
}
